// src/screens/seller/support/SellerSupport.jsx
import React, { useState, useEffect, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Row, Col } from "react-bootstrap";
import {
  FaTicketAlt,
  FaChevronDown,
  FaChevronUp,
  FaSearch,
  FaPlus,
  FaEye,
  FaCheckCircle,
  FaHeadset,
  FaPaperPlane,
  FaExclamationCircle,
  FaUser,
  FaEnvelope,
  FaCalendarAlt,
  FaTag,
  FaTimes,
  FaComments,
  FaPaperclip,
} from "react-icons/fa";
import axios from "axios";
import Button from "../../../components/general/button/Button";
import Pagination from "../../../components/general/pagination/Pagination";
import Swal from "sweetalert2";
import "../../../assets/css/seller/support/SellerSupport.css";
import SellerLayout from "../../../layouts/SellerLayout";
import MyContext from "../../../context/MyContext";
import { SellerSupportPageSkeleton } from "../../../components/general/skeleton/SkeletonBlock";

const TableSkeleton = ({ rows = 4, cols = 6 }) => (
  <div className="spt-skeleton-wrap">
    <div className="spt-skel-row spt-skel-row--header">
      {Array(cols)
        .fill(0)
        .map((_, i) => (
          <div key={i} className="spt-skel spt-skel--th" />
        ))}
    </div>
    {Array(rows)
      .fill(0)
      .map((_, r) => (
        <div key={r} className="spt-skel-row">
          {Array(cols)
            .fill(0)
            .map((_, c) => (
              <div key={c} className="spt-skel spt-skel--td" />
            ))}
        </div>
      ))}
  </div>
);

const SellerSupport = () => {
  const navigate = useNavigate();
  const { baseUrl, authToken } = useContext(MyContext);
  const [errorMessage, setErrorMessage] = useState("");
  const [faqs, setFaqs] = useState([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [subject, setSubject] = useState("");
  const [type, setType] = useState("");
  const [description, setDescription] = useState("");
  const [receivedTickets, setReceivedTickets] = useState([]);
  const [myTickets, setMyTickets] = useState([]);
  const [recLoading, setRecLoading] = useState(true);
  const [myLoading, setMyLoading] = useState(true);

  const [recSearch, setRecSearch] = useState("");
  const [recStatus, setRecStatus] = useState("all");
  const [recPage, setRecPage] = useState(1);
  const [recPerPage, setRecPerPage] = useState(5);

  const [mySearch, setMySearch] = useState("");
  const [myStatus, setMyStatus] = useState("all");
  const [myPage, setMyPage] = useState(1);
  const [myPerPage, setMyPerPage] = useState(5);

  const [openFaq, setOpenFaq] = useState(null);

  // Resolve Ticket modal — teacher ka kaha hua note field: seller
  // "resolved" mark karte waqt likhta hai kis ke haq mein resolve hua
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [resolveTicketId, setResolveTicketId] = useState(null);
  const [resolutionNote, setResolutionNote] = useState("");
  const [refundDue, setRefundDue] = useState(false);

  // Received Ticket Modal
  const [showReceivedModal, setShowReceivedModal] = useState(false);
  const [selectedReceivedTicket, setSelectedReceivedTicket] = useState(null);
  const [recChatMessage, setRecChatMessage] = useState("");
  const [recAttachedFile, setRecAttachedFile] = useState(null);
  const [recConversationMessages, setRecConversationMessages] = useState([]);
  const [recConversationId, setRecConversationId] = useState(null);
  const recFileInputRef = useRef(null);
  const recMessagesEndRef = useRef(null);

  // My Ticket Modal
  const [showMyTicketModal, setShowMyTicketModal] = useState(false);
  const [selectedMyTicket, setSelectedMyTicket] = useState(null);
  const [myChatMessage, setMyChatMessage] = useState("");
  const [myAttachedFile, setMyAttachedFile] = useState(null);
  const [myConversationMessages, setMyConversationMessages] = useState([]);
  const [myConversationId, setMyConversationId] = useState(null);
  const myFileInputRef = useRef(null);
  const myMessagesEndRef = useRef(null);

  // Scroll to bottom
  useEffect(() => {
    recMessagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [recConversationMessages]);

  useEffect(() => {
    myMessagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [myConversationMessages]);

  // addTicket — token se seller pehchana jayega, kmseller_id nahi bhejna
  const addTicket = () => {
    const formdata = new FormData();
    formdata.append("subject", subject);
    formdata.append("type", type);
    formdata.append("description", description);
    formdata.append("kmadmin_id", 1);

    axios
      .post(`${baseUrl}/seller/help/addTicket`, formdata, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((response) => {
        if (response.data.hasOwnProperty("error")) {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error,
          });
        } else {
          Swal.fire({
            icon: "success",
            title: "Success",
            text: "Support ticket added successfully!",
          });
          setSubject("");
          setType("");
          setDescription("");
          viewAllMyTickets();
        }
      })
      .catch(() =>
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to add support ticket",
        }),
      );
  };

  // viewAllMyTickets — GET + Bearer
  const viewAllMyTickets = () => {
    setMyLoading(true);
    return axios
      .get(`${baseUrl}/seller/help/viewAllMyTickets`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        if (response.data.hasOwnProperty("error")) {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error,
          });
        } else {
          setMyTickets(response.data.ticket);
        }
        setMyLoading(false);
      })
      .catch(() => {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to load support tickets.",
        });
        setMyLoading(false);
      });
  };

  // viewAllReceivedTickets — GET + Bearer
  const viewAllReceivedTickets = () => {
    setRecLoading(true);
    return axios
      .get(`${baseUrl}/seller/help/viewAllReceivedTickets`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        if (response.data.hasOwnProperty("error")) {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error,
          });
        } else {
          setReceivedTickets(response.data.ticket);
        }
        setRecLoading(false);
      })
      .catch(() => {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to load customer tickets.",
        });
        setRecLoading(false);
      });
  };

  useEffect(() => {
    if (!authToken) {
      setPageLoading(false);
      navigate("/seller/login");
      return;
    }
    setPageLoading(true);
    let completedCalls = 0;
    const markCallDone = () => {
      completedCalls++;
      if (completedCalls === 3) setPageLoading(false);
    };
    viewAllMyTickets().then(markCallDone).catch(markCallDone);
    viewAllReceivedTickets().then(markCallDone).catch(markCallDone);
    viewAllFaqs().then(markCallDone).catch(markCallDone);
  }, [authToken]);

  // updateTicketStatus
  const updateTicketStatus = (ticketId, newStatus, note, isRefundDue) => {
    const formData = new FormData();
    formData.append("id", ticketId);
    formData.append("status", newStatus);
    if (newStatus === "resolved") {
      formData.append("resolution_note", note || "");
      formData.append("refund_due", isRefundDue ? 1 : 0);
    }
    axios
      .post(`${baseUrl}/seller/help/updateTicketStatus`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        if (response.data.success) {
          Swal.fire({
            icon: "success",
            title: "Success",
            text: "Status updated successfully!",
          });
          viewAllReceivedTickets();
          if (selectedReceivedTicket?.id === ticketId) {
            setSelectedReceivedTicket((prev) => ({
              ...prev,
              status: newStatus,
              resolution_note: note,
              refund_due: isRefundDue ? 1 : 0,
            }));
          }
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "Failed to update status.",
          });
        }
      })
      .catch(() =>
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to update status.",
        }),
      );
  };

  // openResolveModal — resolve se pehle note + refund choice mangte hain
  const openResolveModal = (ticketId) => {
    setResolveTicketId(ticketId);
    setResolutionNote("");
    setRefundDue(false);
    setShowResolveModal(true);
  };

  const submitResolveTicket = () => {
    if (!resolutionNote.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Note required",
        text: "Please add a note explaining who the dispute was resolved in favor of.",
      });
      return;
    }
    updateTicketStatus(resolveTicketId, "resolved", resolutionNote, refundDue);
    setShowResolveModal(false);
  };

  // fetchChatForReceivedTicket
  const fetchChatForReceivedTicket = (ticket) => {
    const formData = new FormData();
    formData.append("kmhelpandsupport_id", ticket.id);
    axios
      .post(`${baseUrl}/seller/chats/fetchChatForHelp`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((res) => {
        if (res.data.success) {
          setRecConversationMessages(res.data.chats);
          setRecConversationId(res.data.conversation_id);
        } else {
          setRecConversationMessages([]);
        }
      })
      .catch((err) => console.log(err));
  };

  // fetchChatForMyTicket
  const fetchChatForMyTicket = (ticket) => {
    const formData = new FormData();
    formData.append("kmhelpandsupport_id", ticket.id);
    axios
      .post(`${baseUrl}/seller/chats/fetchChatForHelp`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((res) => {
        if (res.data.success) {
          setMyConversationMessages(res.data.chats);
          setMyConversationId(res.data.conversation_id);
        } else {
          setMyConversationMessages([]);
        }
      })
      .catch((err) => console.log(err));
  };

  // sendReceivedMessage
  const sendReceivedMessage = () => {
    if (!recChatMessage.trim() && !recAttachedFile) return;
    if (!recConversationId) return;
    const formData = new FormData();
    formData.append("kmconversation_id", recConversationId);
    formData.append("send_by", "seller");
    formData.append("message", recChatMessage);
    formData.append("image", recAttachedFile);
    axios
      .post(`${baseUrl}/seller/chat/sendMessage`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((response) => {
        if (response.data.hasOwnProperty("error")) {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error,
          });
        } else {
          setRecChatMessage("");
          setRecAttachedFile(null);
          if (recFileInputRef.current) recFileInputRef.current.value = "";
          fetchChatForReceivedTicket(selectedReceivedTicket);
        }
      });
  };

  const viewAllFaqs = () => {
    return axios
      .get(`${baseUrl}/seller/faq/viewAllFaqs`, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((response) => {
        if (response.data.hasOwnProperty("error")) {
          setErrorMessage(response.data.error);
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error,
          });
        } else {
          setFaqs(response.data.faqs);
        }
      })
      .catch((error) => {
        console.log(error);
        Swal.fire({ icon: "error", title: "Error", text: "FAQS cannot load" });
      });
  };



  // sendMyMessage
  const sendMyMessage = () => {
    if (!myChatMessage.trim() && !myAttachedFile) return;
    if (!myConversationId) return;
    const formData = new FormData();
    formData.append("kmconversation_id", myConversationId);
    formData.append("send_by", "seller");
    formData.append("message", myChatMessage);
    formData.append("image", myAttachedFile);
    axios
      .post(`${baseUrl}/seller/chat/sendMessage`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((response) => {
        if (response.data.hasOwnProperty("error")) {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error,
          });
        } else {
          setMyChatMessage("");
          setMyAttachedFile(null);
          if (myFileInputRef.current) myFileInputRef.current.value = "";
          fetchChatForMyTicket(selectedMyTicket);
        }
      });
  };

  // Format timestamp
  const formatTime = (datetime) => {
    if (!datetime) return "";
    const date = new Date(datetime);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const statusClass = (s) =>
    ({
      open: "spt-status--open",
      in_progress: "spt-status--pending",
      resolved: "spt-status--resolved",
      closed: "spt-status--closed",
    })[s] || "spt-status--open";

  const getStatusLabel = (s) =>
    ({
      open: "Open",
      in_progress: "In Progress",
      resolved: "Resolved",
      closed: "Closed",
    })[s] || s;

  const getStatusOptions = (ticket) => {
    if (ticket?.kmadmin_id) return [];

    switch (ticket?.status) {
      case "pending":
        return ["open"];
      case "open":
        return ["resolved"];
      default:
        return [];
    }
  };

  const ReceivedTicketActionDropdown = ({ ticket, onView }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
      const handler = (e) => {
        if (ref.current && !ref.current.contains(e.target)) setOpen(false);
      };
      document.addEventListener("mousedown", handler);
      return () => document.removeEventListener("mousedown", handler);
    }, []);
    const statusOptions = getStatusOptions(ticket);
    return (
      <div className="help-action-wrapper" ref={ref}>
        <button
          className="help-action-toggle"
          onClick={() => setOpen((p) => !p)}
          aria-label="Actions"
        >
          <span className="help-action-dot" />
          <span className="help-action-dot" />
          <span className="help-action-dot" />
        </button>
        {open && (
          <div className="help-action-dropdown">
            <button
              className="help-action-item"
              onClick={() => {
                onView(ticket);
                setOpen(false);
              }}
            >
              <FaEye className="help-action-icon" /> View Details
            </button>
            {statusOptions.map((statusOption) => (
              <button
                key={statusOption}
                className="help-action-item"
                onClick={() => {
                  if (statusOption === "resolved") {
                    openResolveModal(ticket.id);
                  } else {
                    updateTicketStatus(ticket.id, statusOption);
                  }
                  setOpen(false);
                }}
              >
                <FaCheckCircle className="help-action-icon" /> Mark as{" "}
                {getStatusLabel(statusOption)}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  const filterTickets = (list, search, status) =>
    (list || []).filter(
      (t) =>
        (t.subject?.toLowerCase().includes(search.toLowerCase()) ||
          String(t.id)?.includes(search)) &&
        (status === "all" || t.status === status),
    );

  const paginate = (list, page, perPage) =>
    list.slice((page - 1) * perPage, page * perPage);

  const filteredRec = filterTickets(receivedTickets, recSearch, recStatus);
  const filteredMy = filterTickets(myTickets, mySearch, myStatus);
  const pageRec = paginate(filteredRec, recPage, recPerPage);
  const pageMy = paginate(filteredMy, myPage, myPerPage);

  if (pageLoading) {
    return (
      <SellerLayout>
        <SellerSupportPageSkeleton />
      </SellerLayout>
    );
  }

  return (
    <SellerLayout>
      <div className="support-container">
        <Container fluid>
          <div className="support-header">
            <h1>Help & Support</h1>
            <p>Manage customer tickets and get help from our team</p>
          </div>

          {/* TABLE 1 — Received Tickets */}
          <div className="spt-section">
            <div className="spt-section__header">
              <div className="spt-section__title">
                <FaExclamationCircle className="spt-section__icon spt-section__icon--red" />
                <h4>Received Tickets</h4>
                <span className="spt-section__count">
                  {receivedTickets.length}
                </span>
              </div>
              <div className="spt-toolbar">
                <div className="spt-search">
                  <FaSearch />
                  <input
                    type="text"
                    placeholder="Search received tickets..."
                    value={recSearch}
                    onChange={(e) => {
                      setRecSearch(e.target.value);
                      setRecPage(1);
                    }}
                  />
                </div>
                <select
                  className="spt-filter"
                  value={recStatus}
                  onChange={(e) => {
                    setRecStatus(e.target.value);
                    setRecPage(1);
                  }}
                >
                  <option value="all">All Status</option>
                  <option value="open">Open</option>
                  <option value="in_progress">In Progress</option>
                  <option value="resolved">Resolved</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
            </div>

            {recLoading ? (
              <TableSkeleton rows={4} cols={8} />
            ) : (
              <div className="help-table-wrapper">
                <table className="ksa-support-table">
                  <thead>
                    <tr>
                      <th>Ticket ID</th>
                      <th>Raised by</th>
                      <th>Order ID</th>
                      <th>Subject</th>
                      <th>Status</th>
                      <th>Date</th>
                      <th>Forwarded</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRec.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="empty-state">
                          No tickets found
                        </td>
                      </tr>
                    ) : (
                      pageRec.map((ticket) => (
                        <tr key={ticket.id}>
                          <td className="ksa-support-ticket-id">{ticket.id}</td>
                          <td>
                            <div className="spt-customer">
                              <strong>
                                {ticket.kmcustomer?.name || "N/A"}
                              </strong>
                              <small>{ticket.kmcustomer?.email || ""}</small>
                            </div>
                          </td>
                          <td className="spt-order-id">
                            #{ticket.kmorder?.id || "N/A"}
                          </td>
                          <td className="ksa-support-subject">
                            {ticket.subject}
                          </td>
                          <td>
                            <span
                              className={`spt-status ${statusClass(ticket.status)}`}
                            >
                              {getStatusLabel(ticket.status)}
                            </span>
                          </td>
                          <td className="ksa-support-date">
                            {ticket.created_at?.split("T")[0]}
                          </td>
                          <td>{ticket.kmadmin_id ? "Yes" : "No"}</td>
                          <td>
                            <ReceivedTicketActionDropdown
                              ticket={ticket}
                              onView={(ticket) => {
                                setSelectedReceivedTicket(ticket);
                                setRecChatMessage("");
                                setRecAttachedFile(null);
                                setShowReceivedModal(true);
                                fetchChatForReceivedTicket(ticket);
                              }}
                            />
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
            <Pagination
              totalItems={filteredRec.length}
              itemsPerPage={recPerPage}
              currentPage={recPage}
              onPageChange={setRecPage}
              onItemsPerPageChange={(n) => {
                setRecPerPage(n);
                setRecPage(1);
              }}
            />
          </div>

          {/* TABLE 2 — My Tickets */}
          <div className="spt-section">
            <div className="spt-section__header">
              <div className="spt-section__title">
                <FaTicketAlt className="spt-section__icon spt-section__icon--blue" />
                <h4>My Tickets</h4>
                <span className="spt-section__count">{myTickets.length}</span>
              </div>
              <div className="spt-toolbar">
                <div className="spt-search">
                  <FaSearch />
                  <input
                    type="text"
                    placeholder="Search my tickets..."
                    value={mySearch}
                    onChange={(e) => {
                      setMySearch(e.target.value);
                      setMyPage(1);
                    }}
                  />
                </div>
                <select
                  className="spt-filter"
                  value={myStatus}
                  onChange={(e) => {
                    setMyStatus(e.target.value);
                    setMyPage(1);
                  }}
                >
                  <option value="all">All Status</option>
                  <option value="open">Open</option>
                  <option value="in_progress">In Progress</option>
                  <option value="resolved">Resolved</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
            </div>

            {myLoading ? (
              <TableSkeleton rows={3} cols={6} />
            ) : (
              <div className="help-table-wrapper">
                <table className="ksa-support-table">
                  <thead>
                    <tr>
                      <th>id</th>
                      <th>Subject</th>
                      <th>Type</th>
                      <th>Date</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageMy.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="empty-state">
                          No tickets found
                        </td>
                      </tr>
                    ) : (
                      pageMy.map((ticket) => (
                        <tr key={ticket.id}>
                          <td className="ksa-support-ticket-id">{ticket.id}</td>
                          <td className="ksa-support-subject">
                            {ticket.subject}
                          </td>
                          <td className="ksa-support-category">
                            {ticket.type}
                          </td>
                          <td className="ksa-support-date">
                            {ticket.created_at?.split("T")[0]}
                          </td>
                          <td>
                            <span
                              className={`spt-status ${statusClass(ticket.status)}`}
                            >
                              {getStatusLabel(ticket.status)}
                            </span>
                          </td>
                          <td>
                            {/* Sirf ek hi action — dropdown ki jagah direct icon */}
                            <button
                              className="ksa-icon-action-btn"
                              title="View Details"
                              onClick={() => {
                                setSelectedMyTicket(ticket);
                                setMyChatMessage("");
                                setMyAttachedFile(null);
                                setShowMyTicketModal(true);
                                fetchChatForMyTicket(ticket);
                              }}
                            >
                              <FaEye />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
            <Pagination
              totalItems={filteredMy.length}
              itemsPerPage={myPerPage}
              currentPage={myPage}
              onPageChange={setMyPage}
              onItemsPerPageChange={(n) => {
                setMyPerPage(n);
                setMyPage(1);
              }}
            />
          </div>

          {/* FAQs + Create Ticket */}
          <Row className="g-4">
            <Col lg={6}>
              <div className="spt-section spt-section--faq">
                <div className="spt-section__header">
                  <div className="spt-section__title">
                    <FaHeadset className="spt-section__icon spt-section__icon--green" />
                    <h4>Frequently Asked Questions</h4>
                  </div>
                </div>
                <div className="spt-faq-list">
                  {faqs.map((faq, i) => (
                    <div key={i} className="spt-faq-item">
                      <div
                        className="spt-faq-question"
                        onClick={() => setOpenFaq(openFaq === i ? null : i)}
                      >
                        <span>{faq.question}</span>
                        {openFaq === i ? <FaChevronUp /> : <FaChevronDown />}
                      </div>
                      {openFaq === i && (
                        <div className="spt-faq-answer">{faq.answer}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </Col>

            <Col lg={6}>
              <div className="spt-section spt-section--form">
                <div className="spt-section__header">
                  <div className="spt-section__title">
                    <FaPlus className="spt-section__icon spt-section__icon--blue" />
                    <h4>Create Support Ticket</h4>
                  </div>
                </div>
                <div className="spt-create-form">
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      if (!subject || !type || !description) {
                        Swal.fire({
                          title: "Error!",
                          text: "Please fill all fields.",
                          icon: "error",
                        });
                        return;
                      }
                      if (!authToken) {
                        Swal.fire({
                          title: "Error!",
                          text: "Please login again.",
                          icon: "error",
                        });
                        return;
                      }
                      addTicket();
                    }}
                  >
                    <div className="spt-form-group">
                      <label>Subject *</label>
                      <input
                        type="text"
                        placeholder="Enter ticket subject"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                      />
                    </div>
                    <div className="spt-form-group">
                      <label>Type *</label>
                      <select
                        value={type}
                        onChange={(e) => setType(e.target.value)}
                      >
                        <option value="">Select</option>
                        <option value="Payment">Payment</option>
                        <option value="Technical">Technical</option>
                        <option value="Account">Account</option>
                        <option value="Product">Product</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div className="spt-form-group">
                      <label>Description *</label>
                      <textarea
                        rows="5"
                        placeholder="Describe your issue in detail..."
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                      />
                    </div>
                    <Button
                      type="submit"
                      variant="primary"
                      fullWidth
                      icon={<FaPaperPlane size={14} />}
                    >
                      Submit Ticket
                    </Button>
                  </form>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Received Ticket Modal */}
      {showReceivedModal && selectedReceivedTicket && (
        <div
          className="ksa-modal-overlay"
          onClick={() => setShowReceivedModal(false)}
        >
          <div
            className="ksa-modal ksa-modal-lg ksa-support-view-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ksa-support-modal-header">
              <div>
                <h3 className="ksa-support-modal-title">Ticket Details</h3>
                <p className="ksa-support-modal-number">
                  {selectedReceivedTicket.id}
                </p>
              </div>
              <button
                className="ksa-modal-close"
                onClick={() => setShowReceivedModal(false)}
              >
                <FaTimes size={18} />
              </button>
            </div>

            <div className="ksa-support-modal-body">
              <Row className="g-4">
                <Col xs={12} md={4}>
                  <div className="ksa-support-info-card">
                    <h5>Ticket Information</h5>
                    <div className="ksa-support-info-item">
                      <FaTag />
                      <div>
                        <label>Subject</label>
                        <p>{selectedReceivedTicket.subject}</p>
                      </div>
                    </div>
                    <div className="ksa-support-info-item">
                      <FaCalendarAlt />
                      <div>
                        <label>Created</label>
                        <p>
                          {new Date(
                            selectedReceivedTicket.created_at,
                          ).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <div className="ksa-support-info-item">
                      <FaUser />
                      <div>
                        <label>Raised by</label>
                        <p>
                          {selectedReceivedTicket.kmcustomer?.name || "N/A"}
                        </p>
                      </div>
                    </div>
                    <div className="ksa-support-info-item">
                      <FaEnvelope />
                      <div>
                        <label>Email</label>
                        <p>
                          {selectedReceivedTicket.kmcustomer?.email || "N/A"}
                        </p>
                      </div>
                    </div>
                    <div className="ksa-support-info-item">
                      <FaTag />
                      <div>
                        <label>Order ID</label>
                        <p className="spt-order-id">
                          #{selectedReceivedTicket.kmorder?.id || "N/A"}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="ksa-support-status-card">
                    <h5>Status</h5>
                    <div className="ksa-support-badges">
                      <span
                        className={`spt-status ${statusClass(selectedReceivedTicket.status)}`}
                      >
                        {getStatusLabel(selectedReceivedTicket.status)}
                      </span>
                    </div>
                  </div>
                </Col>

                <Col xs={12} md={8}>
                  <div className="ksa-support-chat-section">
                    <h5 className="ksa-support-chat-title">
                      <FaComments /> Conversation
                    </h5>
                    <div className="ksa-support-subject-banner">
                      <strong>Subject:</strong> {selectedReceivedTicket.subject}
                    </div>
                    <div className="ksa-support-chat-container">
                      {recConversationMessages?.map((msg) => (
                        <div
                          key={msg.id}
                          className={`ksa-support-chat-message ${msg.send_by === "seller" ? "user" : "admin"}`}
                        >
                          <div className="ksa-support-chat-bubble">
                            {msg.send_by !== "seller" && (
                              <small
                                style={{
                                  fontWeight: "600",
                                  color: "#666",
                                  display: "block",
                                  marginBottom: "4px",
                                }}
                              >
                                {msg.send_by === "customer"
                                  ? selectedReceivedTicket?.kmcustomer?.name ||
                                    "Customer"
                                  : "Admin"}
                              </small>
                            )}
                            {msg.image && (
                              <img
                                src={`${baseUrl}/storage/${msg.image}`}
                                alt="attachment"
                                style={{
                                  maxWidth: "200px",
                                  borderRadius: "8px",
                                  marginBottom: "4px",
                                }}
                              />
                            )}
                            {msg.message && <p>{msg.message}</p>}
                            <span className="ksa-support-chat-time">
                              {formatTime(msg.created_at)}
                            </span>
                          </div>
                        </div>
                      ))}
                      <div ref={recMessagesEndRef} />
                    </div>

                    {selectedReceivedTicket.status !== "resolved" &&
                    selectedReceivedTicket.status !== "closed" ? (
                      <div className="ksa-support-chat-input-wrapper">
                        {recAttachedFile && (
                          <div className="ksa-support-attached-file">
                            <FaPaperclip />
                            <span>{recAttachedFile.name}</span>
                            <button
                              onClick={() => {
                                setRecAttachedFile(null);
                                if (recFileInputRef.current)
                                  recFileInputRef.current.value = "";
                              }}
                            >
                              <FaTimes />
                            </button>
                          </div>
                        )}
                        <div className="ksa-support-chat-input-container">
                          <input
                            type="file"
                            ref={recFileInputRef}
                            style={{ display: "none" }}
                            onChange={(e) =>
                              setRecAttachedFile(e.target.files[0] || null)
                            }
                            accept="image/*,.pdf,.doc,.docx"
                          />
                          <label
                            className="ksa-support-attach-btn"
                            onClick={() => recFileInputRef.current.click()}
                          >
                            <FaPaperclip />
                          </label>
                          <input
                            type="text"
                            className="ksa-support-chat-input"
                            placeholder="Type your message..."
                            value={recChatMessage}
                            onChange={(e) => setRecChatMessage(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                sendReceivedMessage();
                              }
                            }}
                          />
                          <button
                            className="ksa-support-chat-send-btn"
                            onClick={sendReceivedMessage}
                            disabled={
                              !recChatMessage.trim() && !recAttachedFile
                            }
                          >
                            <FaPaperPlane />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="ksa-support-ticket-resolved">
                        <FaCheckCircle /> Ticket{" "}
                        {selectedReceivedTicket.status === "resolved"
                          ? "Resolved"
                          : "Closed"}
                      </div>
                    )}
                  </div>
                </Col>
              </Row>
            </div>
            <div className="ksa-support-modal-footer">
              <Button
                variant="secondary"
                onClick={() => setShowReceivedModal(false)}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* My Ticket Modal */}
      {showMyTicketModal && selectedMyTicket && (
        <div
          className="ksa-modal-overlay"
          onClick={() => setShowMyTicketModal(false)}
        >
          <div
            className="ksa-modal ksa-modal-lg ksa-support-view-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ksa-support-modal-header">
              <div>
                <h3 className="ksa-support-modal-title">Ticket Details</h3>
                <p className="ksa-support-modal-number">
                  {selectedMyTicket.id}
                </p>
              </div>
              <button
                className="ksa-modal-close"
                onClick={() => setShowMyTicketModal(false)}
              >
                <FaTimes size={18} />
              </button>
            </div>

            <div className="ksa-support-modal-body">
              <Row className="g-4">
                <Col xs={12} md={4}>
                  <div className="ksa-support-info-card">
                    <h5>Ticket Information</h5>
                    <div className="ksa-support-info-item">
                      <FaTicketAlt />
                      <div>
                        <label>Subject</label>
                        <p>{selectedMyTicket.subject}</p>
                      </div>
                    </div>
                    <div className="ksa-support-info-item">
                      <FaCalendarAlt />
                      <div>
                        <label>Created</label>
                        <p>
                          {new Date(
                            selectedMyTicket.created_at,
                          ).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <div className="ksa-support-info-item">
                      <FaComments />
                      <div>
                        <label>Description</label>
                        <p>{selectedMyTicket.description}</p>
                      </div>
                    </div>
                  </div>
                  <div className="ksa-support-status-card">
                    <h5>Status</h5>
                    <div className="ksa-support-badges">
                      <span
                        className={`spt-status ${statusClass(selectedMyTicket.status)}`}
                      >
                        {getStatusLabel(selectedMyTicket.status)}
                      </span>
                    </div>
                  </div>
                </Col>

                <Col xs={12} md={8}>
                  <div className="ksa-support-chat-section">
                    <h5 className="ksa-support-chat-title">
                      <FaComments /> Conversation
                    </h5>
                    <div className="ksa-support-subject-banner">
                      <strong>Type:</strong> {selectedMyTicket.type}
                    </div>
                    <div className="ksa-support-chat-container">
                      {myConversationMessages?.map((msg) => (
                        <div
                          key={msg.id}
                          className={`ksa-support-chat-message ${msg.send_by === "seller" ? "user" : "admin"}`}
                        >
                          <div className="ksa-support-chat-bubble">
                            {msg.send_by !== "seller" && (
                              <small
                                style={{
                                  fontWeight: "600",
                                  color: "#666",
                                  display: "block",
                                  marginBottom: "4px",
                                }}
                              >
                                Admin
                              </small>
                            )}
                            {msg.image && (
                              <img
                                src={`${baseUrl}/storage/${msg.image}`}
                                alt="attachment"
                                style={{
                                  maxWidth: "200px",
                                  borderRadius: "8px",
                                  marginBottom: "4px",
                                }}
                              />
                            )}
                            {msg.message && <p>{msg.message}</p>}
                            <span className="ksa-support-chat-time">
                              {formatTime(msg.created_at)}
                            </span>
                          </div>
                        </div>
                      ))}
                      <div ref={myMessagesEndRef} />
                    </div>

                    {selectedMyTicket.status !== "resolved" &&
                    selectedMyTicket.status !== "closed" ? (
                      <div className="ksa-support-chat-input-wrapper">
                        {myAttachedFile && (
                          <div className="ksa-support-attached-file">
                            <FaPaperclip />
                            <span>{myAttachedFile.name}</span>
                            <button
                              onClick={() => {
                                setMyAttachedFile(null);
                                if (myFileInputRef.current)
                                  myFileInputRef.current.value = "";
                              }}
                            >
                              <FaTimes />
                            </button>
                          </div>
                        )}
                        <div className="ksa-support-chat-input-container">
                          <input
                            type="file"
                            ref={myFileInputRef}
                            style={{ display: "none" }}
                            onChange={(e) =>
                              setMyAttachedFile(e.target.files[0] || null)
                            }
                            accept="image/*,.pdf,.doc,.docx"
                          />
                          <label
                            className="ksa-support-attach-btn"
                            onClick={() => myFileInputRef.current.click()}
                          >
                            <FaPaperclip />
                          </label>
                          <input
                            type="text"
                            className="ksa-support-chat-input"
                            placeholder="Type your message..."
                            value={myChatMessage}
                            onChange={(e) => setMyChatMessage(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                sendMyMessage();
                              }
                            }}
                          />
                          <button
                            className="ksa-support-chat-send-btn"
                            onClick={sendMyMessage}
                            disabled={!myChatMessage.trim() && !myAttachedFile}
                          >
                            <FaPaperPlane />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="ksa-support-ticket-resolved">
                        <FaCheckCircle /> Ticket{" "}
                        {selectedMyTicket.status === "resolved"
                          ? "Resolved"
                          : "Closed"}
                      </div>
                    )}
                  </div>
                </Col>
              </Row>
            </div>
            <div className="ksa-support-modal-footer">
              <Button
                variant="secondary"
                onClick={() => setShowMyTicketModal(false)}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Resolve Ticket Modal — note + refund-due choice */}
      {showResolveModal && (
        <div
          className="ksa-modal-overlay"
          onClick={() => setShowResolveModal(false)}
        >
          <div className="ksa-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ksa-support-modal-header">
              <h3 className="ksa-support-modal-title">Resolve Ticket</h3>
              <button
                className="ksa-modal-close"
                onClick={() => setShowResolveModal(false)}
              >
                <FaTimes />
              </button>
            </div>
            <div className="ksa-support-modal-body">
              <label>Resolution note (who is this resolved in favor of?)</label>
              <textarea
                className="form-control"
                rows={4}
                placeholder="e.g. Customer's claim was valid, item was damaged — refund is due."
                value={resolutionNote}
                onChange={(e) => setResolutionNote(e.target.value)}
              />
              <div style={{ marginTop: "12px" }}>
                <label>
                  <input
                    type="checkbox"
                    checked={refundDue}
                    onChange={(e) => setRefundDue(e.target.checked)}
                    style={{ marginRight: "8px" }}
                  />
                  Customer's refund is due (forward to Admin automatically)
                </label>
              </div>
            </div>
            <div className="ksa-support-modal-footer">
              <Button
                variant="secondary"
                onClick={() => setShowResolveModal(false)}
              >
                Cancel
              </Button>
              <Button variant="success" onClick={submitResolveTicket}>
                Confirm Resolve
              </Button>
            </div>
          </div>
        </div>
      )}
    </SellerLayout>
  );
};

export default SellerSupport;
